import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterAlt as FilterIcon, Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getExchanges, getPairs, type GetPairsParams } from '@/services/api';
import { PageShell, SectionHeader } from '@/components/common/Page';
import ExchangeLogo from '@/components/common/ExchangeLogo';
import PairCard from '@/components/pairs/PairCard';
import { useI18n } from '@/contexts/I18nContext';
import type { ArbitragePair } from '@/types';

type PairTypeFilter = 'all' | 'single' | 'double';
type PairStatusFilter = 'all' | 'active' | 'inactive';
type PairSort = 'display_order' | 'spread_asc' | 'spread_desc';

const LIVE_PAIR_REFRESH_MS = 30_000;

export default function PairsPage() {
  const { plural, t } = useI18n();
  const [searchCoin, setSearchCoin] = useState('');
  const [filterExchange, setFilterExchange] = useState('');
  const [filterType, setFilterType] = useState<PairTypeFilter>('all');
  const [filterStatus, setFilterStatus] = useState<PairStatusFilter>('active');
  const [sortBy, setSortBy] = useState<PairSort>('display_order');

  const { data: exchangesData } = useQuery({
    queryKey: ['exchanges'],
    queryFn: getExchanges,
  });

  const params: GetPairsParams = {
    ...(searchCoin && { coin: searchCoin }),
    ...(filterExchange && { exchange: filterExchange }),
    ...(filterType !== 'all' && { type: filterType }),
    ...(filterStatus !== 'all' && { status: filterStatus }),
    sort: sortBy,
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pairs', params],
    queryFn: () => getPairs(params),
    refetchInterval: LIVE_PAIR_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
    staleTime: 0,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const basicPairs = data?.pairs.filter((pair: ArbitragePair) => pair.access_level === 'basic') ?? [];
  const privatePairs = data?.pairs.filter((pair: ArbitragePair) => pair.access_level === 'private') ?? [];
  const totalPairs = data?.pairs.length ?? 0;
  const getRouteWord = (count: number) =>
    plural(count, {
      one: 'common.routeOne',
      few: 'common.routeFew',
      other: 'common.routeOther',
    });
  const statusLabel =
    filterStatus === 'active'
      ? t('status.activeLower')
      : filterStatus === 'inactive'
        ? t('status.inactiveLower')
        : '';
  const routeCountLabel =
    filterStatus === 'all'
      ? t('pairs.routeCount', { count: totalPairs, routeWord: getRouteWord(totalPairs) })
      : t('pairs.filteredRouteCount', {
          count: totalPairs,
          status: statusLabel,
          routeWord: getRouteWord(totalPairs),
        });

  const renderPairSection = (title: string, pairs: ArbitragePair[]) => {
    if (pairs.length === 0) return null;

    return (
      <>
        <SectionHeader
          title={title}
          detail={t('pairs.sectionCount', { count: pairs.length, routeWord: getRouteWord(pairs.length) })}
        />
        <Stack spacing={1.25}>
          {pairs.map((pair) => (
            <PairCard key={pair.id} pair={pair} onUpdate={refetch} />
          ))}
        </Stack>
      </>
    );
  };

  return (
    <PageShell
      eyebrow={t('pairs.eyebrow')}
      title={t('pairs.title')}
      showAnnouncements
      action={<Chip icon={<FilterIcon />} label={routeCountLabel} color="primary" />}
    >
      <Box
        sx={{
          p: 1.25,
          mb: 2,
          borderRadius: 2,
          border: '1px solid rgba(226, 255, 238, 0.12)',
          bgcolor: 'rgba(244, 255, 249, 0.045)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: '2fr repeat(4, 1fr)' },
            gap: 1,
          }}
        >
          <TextField
            size="small"
            placeholder={t('pairs.searchCoin')}
            value={searchCoin}
            onChange={(event) => setSearchCoin(event.target.value.toUpperCase())}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel>{t('pairs.exchange')}</InputLabel>
            <Select
              value={filterExchange}
              label={t('pairs.exchange')}
              onChange={(event) => setFilterExchange(event.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {exchangesData?.exchanges.map((exchange) => (
                <MenuItem key={exchange.id} value={exchange.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ExchangeLogo src={exchange.logo_url} name={exchange.name} id={exchange.id} size={22} />
                    <Typography>{exchange.name}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>{t('pairs.type')}</InputLabel>
            <Select
              value={filterType}
              label={t('pairs.type')}
              onChange={(event) => setFilterType(event.target.value as PairTypeFilter)}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              <MenuItem value="single">{t('pairs.typeSingle')}</MenuItem>
              <MenuItem value="double">{t('pairs.typeDouble')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>{t('pairs.status')}</InputLabel>
            <Select
              value={filterStatus}
              label={t('pairs.status')}
              onChange={(event) => setFilterStatus(event.target.value as PairStatusFilter)}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              <MenuItem value="active">{t('status.active')}</MenuItem>
              <MenuItem value="inactive">{t('status.inactive')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>{t('pairs.sort')}</InputLabel>
            <Select
              value={sortBy}
              label={t('pairs.sort')}
              onChange={(event) => setSortBy(event.target.value as PairSort)}
            >
              <MenuItem value="display_order">{t('pairs.sortDefault')}</MenuItem>
              <MenuItem value="spread_desc">{t('pairs.sortSpreadHigh')}</MenuItem>
              <MenuItem value="spread_asc">{t('pairs.sortSpreadLow')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{t('pairs.failedToLoad')}</Alert>
      ) : totalPairs === 0 ? (
        <Alert severity="info">{t('pairs.noneFound')}</Alert>
      ) : (
        <>
          {renderPairSection(t('pairs.sectionBasic'), basicPairs)}
          {renderPairSection(t('pairs.sectionPrivate'), privatePairs)}
        </>
      )}
    </PageShell>
  );
}
